import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useToast } from '@redpandev/ui';
import { usePwaInstall } from '../../lib/pwa-install';
import {
  isInstallPromptEnabled,
  isPermanentEntryAvailable,
  recordDismissed,
  recordVisit,
  shouldAutoShow,
} from '../../lib/pwa-install-prompt';
import { InstallPrompt, type InstallPromptCloseReason } from './InstallPrompt';

/** Délai avant l'ouverture auto : laisse la page se poser (moins agressif). */
const AUTO_SHOW_DELAY_MS = 600;

interface InstallPromptContextValue {
  /** Ouvre la popin sans condition d'engagement (entrée permanente). */
  open: () => void;
  /** L'entrée permanente doit-elle apparaître (plateforme capable, non installé) ? */
  available: boolean;
}

const InstallPromptContext = createContext<InstallPromptContextValue>({
  open: () => {},
  available: false,
});

/** Accès à l'entrée permanente « Installer l'app » (menu/réglages). */
export function useInstallPrompt(): InstallPromptContextValue {
  return useContext(InstallPromptContext);
}

/**
 * Orchestre la popin d'installation (spec 12 §4/§5) : déclenchement automatique
 * (engagement, non harcelant), snooze, entrée permanente, toast à l'installation.
 * Tout est neutralisé tant que `VITE_PWA_INSTALL_PROMPT` n'est pas `on` → aucun rendu,
 * aucune trace en prod. Monté une seule fois autour de l'app (main.tsx).
 */
export function InstallPromptProvider({ children }: { children: ReactNode }) {
  const enabled = isInstallPromptEnabled();
  const { platform, isInstalled, canPrompt } = usePwaInstall();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const shownThisSession = useRef(false);
  const openedAuto = useRef(false);

  // Enregistre le premier jour de visite (déclencheur « 2ᵉ visite »). No-op si flag off.
  useEffect(() => {
    recordVisit();
  }, []);

  // Toast « installé 🐾 » — que l'installation vienne de notre popin ou du menu navigateur.
  useEffect(() => {
    if (!enabled) return;
    const onInstalled = () => push({ intent: 'success', title: 'redpan.dev est installé 🐾' });
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, [enabled, push]);

  // Déclenchement automatique, au plus une fois par session. Ré-évalué si `canPrompt`
  // arrive tard (Chromium) ou si l'état d'installation change. Ouverture DIFFÉRÉE
  // (pas dans le corps de l'effet) : moins abrupt à l'arrivée + pas de cascade de rendu.
  useEffect(() => {
    if (shownThisSession.current) return;
    if (!shouldAutoShow({ platform, isInstalled, canPrompt })) return;
    shownThisSession.current = true;
    openedAuto.current = true;
    const timer = setTimeout(() => setOpen(true), AUTO_SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [platform, isInstalled, canPrompt]);

  const handleClose = (reason: InstallPromptCloseReason) => {
    // Seuls les refus d'une popin AUTO comptent vers le snooze (pas l'entrée permanente).
    if (openedAuto.current && reason !== 'installed') recordDismissed();
    openedAuto.current = false;
    setOpen(false);
  };

  const available = enabled && isPermanentEntryAvailable({ platform, isInstalled, canPrompt });

  const value = useMemo<InstallPromptContextValue>(
    () => ({
      open: () => {
        shownThisSession.current = true;
        openedAuto.current = false;
        setOpen(true);
      },
      available,
    }),
    [available],
  );

  const canRenderPopin = enabled && (platform === 'chromium' || platform === 'ios');

  return (
    <InstallPromptContext.Provider value={value}>
      {children}
      {canRenderPopin && (
        <InstallPrompt
          open={open}
          platform={platform as 'chromium' | 'ios'}
          onClose={handleClose}
        />
      )}
    </InstallPromptContext.Provider>
  );
}
